import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar, User, ChevronLeft, ChevronRight, AlertCircle, CheckCircle2,
  Clock, X, Info
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS_OF_WEEK = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

const MinisterAvailability = () => {
  const [ministers, setMinisters] = useState([]);
  const [selectedMinister, setSelectedMinister] = useState(null);
  const [ministerName, setMinisterName] = useState('');
  const [matchedMinister, setMatchedMinister] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [step, setStep] = useState(1); // 1 = select minister, 2 = calendar

  // Load ministers list
  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/ministers`);
        const data = await res.json();
        setMinisters(data.filter(m => m.active !== false));
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, []);

  // Load minister's unavailable dates and assignments when selected
  const loadMinisterData = useCallback(async (minister) => {
    try {
      // Get existing unavailable dates
      setUnavailableDates(minister.unavailable_dates || []);

      // Get minister's upcoming assignments
      const res = await fetch(`${API}/rota/minister-assignments/${minister.id}`);
      if (res.ok) {
        const data = await res.json();
        setAssignments(data.assignments || []);
      }
    } catch (e) { console.error(e); }
  }, []);

  // Match minister by name
  const handleFindMinister = () => {
    setError('');
    if (!ministerName.trim()) { setError('Please enter your name'); return; }
    
    const name = ministerName.trim().toLowerCase();
    const match = ministers.find(m => 
      m.name.toLowerCase() === name || 
      m.name.toLowerCase().includes(name) ||
      name.includes(m.name.toLowerCase())
    );
    
    if (!match) {
      setError('Minister not found. Please enter your name exactly as it appears on the rota.');
      return;
    }
    
    setMatchedMinister(match);
    setSelectedMinister(match);
    loadMinisterData(match);
    setStep(2);
  };

  // Get days in month
  const getDaysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (month, year) => {
    const day = new Date(year, month, 1).getDay();
    return day === 0 ? 6 : day - 1; // Monday-based
  };

  // Check if date is within 7-day cutoff
  const isWithinCutoff = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const diff = (date - now) / (1000 * 60 * 60 * 24);
    return diff < 7;
  };

  // Check if date is in the past
  const isPast = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return date < now;
  };

  // Check if date has an assignment
  const getAssignment = (dateStr) => {
    return assignments.find(a => a.date === dateStr);
  };

  // Toggle unavailability for a date
  const toggleDate = async (dateStr) => {
    setError('');
    setSuccess('');

    if (isPast(dateStr)) { setError('Cannot change availability for past dates'); return; }
    if (isWithinCutoff(dateStr)) { setError('Changes must be made at least 7 days before the service date'); return; }

    setSaving(true);
    const isCurrentlyUnavailable = unavailableDates.includes(dateStr);

    // Auth headers — pick up either an admin session (auto-attached by
    // /src/index.js axios interceptor for logged-in admins) OR a signed
    // minister self-service token (delivered by admin via availability email,
    // passed as ?t=<token> in the URL and cached in localStorage).
    const url = new URL(window.location.href);
    const urlToken = url.searchParams.get('t');
    if (urlToken) {
      try { localStorage.setItem(`ministerToken:${matchedMinister.id}`, urlToken); } catch (_) {}
    }
    const cachedToken = (() => {
      try { return localStorage.getItem(`ministerToken:${matchedMinister.id}`) || ''; } catch (_) { return ''; }
    })();
    const adminToken = (() => {
      try { return localStorage.getItem('adminToken') || localStorage.getItem('admin_token') || ''; } catch (_) { return ''; }
    })();
    const authHeaders = {
      'Content-Type': 'application/json',
      ...(adminToken ? { 'x-admin-token': adminToken } : {}),
      ...(cachedToken ? { 'x-minister-token': cachedToken } : {}),
    };

    try {
      let res;
      if (isCurrentlyUnavailable) {
        // Remove unavailability using the scoped endpoint
        res = await fetch(`${API}/ministers/${matchedMinister.id}/unavailable/${encodeURIComponent(dateStr)}`, {
          method: 'DELETE',
          headers: authHeaders,
        });
      } else {
        // Add unavailability using the scoped endpoint
        res = await fetch(`${API}/ministers/${matchedMinister.id}/unavailable`, {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({ dates: [dateStr] }),
        });
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Request failed (${res.status})`);
      }
      const data = await res.json();
      setUnavailableDates(data.unavailable_dates || []);
      setSuccess(isCurrentlyUnavailable ? 'Date marked as available' : 'Date marked as unavailable');
    } catch (e) {
      setError(e.message?.includes('token') ? e.message : 'Failed to save. If this keeps happening, please ask an admin to email you a new availability link.');
    }
    setSaving(false);
    setTimeout(() => setSuccess(''), 3000);
  };

  // Navigate months
  const prevMonth = () => {
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(y => y - 1); }
    else setCurrentMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(y => y + 1); }
    else setCurrentMonth(m => m + 1);
  };

  // Render calendar
  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const cells = [];

    // Empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) cells.push(<div key={`e-${i}`} />);

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const isUnavailable = unavailableDates.includes(dateStr);
      const assignment = getAssignment(dateStr);
      const past = isPast(dateStr);
      const locked = isWithinCutoff(dateStr) && !past;
      const isToday = dateStr === new Date().toISOString().slice(0, 10);

      cells.push(
        <button
          key={day}
          onClick={() => !past && toggleDate(dateStr)}
          disabled={past || saving}
          data-testid={`cal-day-${dateStr}`}
          className={`relative p-1.5 sm:p-2 rounded-xl text-center transition-all min-h-[60px] sm:min-h-[72px] flex flex-col items-center justify-start gap-0.5 ${
            past ? 'opacity-30 cursor-not-allowed bg-slate-50' :
            isUnavailable ? 'bg-red-100 border-2 border-red-400 hover:bg-red-200' :
            assignment ? 'bg-blue-50 border-2 border-blue-300 hover:bg-blue-100' :
            locked ? 'bg-amber-50 border border-amber-200' :
            'bg-white border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50'
          }`}
        >
          <span className={`text-sm font-bold ${
            isToday ? 'w-7 h-7 rounded-full bg-indigo-600 text-white flex items-center justify-center' :
            isUnavailable ? 'text-red-700' :
            past ? 'text-slate-400' : 'text-slate-800'
          }`}>{day}</span>
          {assignment && !isUnavailable && (
            <span className="text-[10px] leading-tight text-blue-600 font-medium truncate w-full px-0.5">
              {assignment.role_display?.replace('Service ', '').replace('Bible Lesson ', 'Lesson ') || assignment.role}
            </span>
          )}
          {isUnavailable && (
            <span className="text-[10px] text-red-600 font-bold">UNAVAILABLE</span>
          )}
          {locked && !past && !isUnavailable && (
            <Clock className="w-3 h-3 text-amber-400 absolute top-1 right-1" />
          )}
        </button>
      );
    }
    return cells;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-violet-50 to-amber-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-3 border-indigo-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-violet-50 to-amber-50 py-6 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-indigo-100 text-indigo-700 text-sm font-medium mb-3">
            <Calendar className="w-4 h-4" /> Minister Availability
          </div>
          <h1 className="text-2xl sm:text-3xl font-black text-slate-900">Service Availability Calendar</h1>
          <p className="text-slate-500 text-sm mt-1">Celestial Church of Christ — Valley of Berachah</p>
        </div>

        <AnimatePresence mode="wait">
          {/* Step 1: Find Minister */}
          {step === 1 && (
            <motion.div key="step1" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
              className="bg-white rounded-3xl shadow-xl border border-slate-100 p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-2xl bg-indigo-100">
                  <User className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-800">Find Your Name</h2>
                  <p className="text-sm text-slate-500">Enter your name as it appears on the church rota</p>
                </div>
              </div>

              {error && (
                <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              <div className="relative mb-4">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Enter your full name..."
                  value={ministerName}
                  onChange={e => setMinisterName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleFindMinister()}
                  data-testid="minister-name-input"
                  className="w-full pl-11 pr-4 py-4 border-2 border-slate-200 rounded-xl focus:border-indigo-400 outline-none text-base"
                />
              </div>

              <button onClick={handleFindMinister} data-testid="minister-find-btn"
                className="w-full py-4 rounded-xl bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 text-violet-950 font-bold text-base hover:from-amber-400 hover:to-yellow-300 transition-all shadow-lg shadow-amber-500/30">
                Continue
              </button>

              <div className="mt-6 pt-4 border-t border-slate-100">
                <p className="text-xs text-slate-400 text-center">Ministers on the rota:</p>
                <div className="flex flex-wrap gap-1.5 mt-2 justify-center">
                  {ministers.slice(0, 15).map(m => (
                    <button key={m.id} onClick={() => { setMinisterName(m.name); }}
                      className="px-2 py-1 rounded-lg bg-slate-50 text-xs text-slate-600 hover:bg-indigo-50 hover:text-indigo-700 transition-colors">
                      {m.name}
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 2: Calendar */}
          {step === 2 && matchedMinister && (
            <motion.div key="step2" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
              {/* Minister Info Bar */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 mb-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-indigo-500 flex items-center justify-center">
                    <span className="text-white font-bold">{matchedMinister.name[0]}</span>
                  </div>
                  <div>
                    <p className="font-bold text-slate-800">{matchedMinister.title} {matchedMinister.name}</p>
                    <p className="text-xs text-slate-500">Tap dates to mark yourself unavailable</p>
                  </div>
                </div>
                <button onClick={() => { setStep(1); setMatchedMinister(null); setMinisterName(''); }}
                  className="p-2 rounded-lg text-slate-400 hover:bg-slate-100">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Legend */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-3 mb-4">
                <div className="flex flex-wrap gap-3 justify-center text-xs">
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 rounded bg-blue-100 border-2 border-blue-300" />
                    <span className="text-slate-600">Assigned service</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 rounded bg-red-100 border-2 border-red-400" />
                    <span className="text-slate-600">Marked unavailable</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4 text-amber-400" />
                    <span className="text-slate-600">Within 7-day lock</span>
                  </div>
                </div>
              </div>

              {/* Status Messages */}
              {error && (
                <div className="mb-3 p-3 rounded-xl bg-red-50 border border-red-200 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}
              {success && (
                <div className="mb-3 p-3 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                  <p className="text-emerald-600 text-sm">{success}</p>
                </div>
              )}

              {/* Calendar */}
              <div className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden">
                {/* Month Navigation */}
                <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-violet-800 via-purple-700 to-violet-800 text-white">
                  <button onClick={prevMonth} className="p-2 rounded-xl hover:bg-white/20 transition-colors" data-testid="cal-prev-month">
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <h3 className="text-lg font-bold">{MONTHS[currentMonth]} {currentYear}</h3>
                  <button onClick={nextMonth} className="p-2 rounded-xl hover:bg-white/20 transition-colors" data-testid="cal-next-month">
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>

                {/* Day Headers */}
                <div className="grid grid-cols-7 gap-1 px-3 pt-3">
                  {DAYS_OF_WEEK.map(d => (
                    <div key={d} className="text-center text-xs font-bold text-slate-400 py-1">{d}</div>
                  ))}
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-1 p-3">
                  {renderCalendar()}
                </div>
              </div>

              {/* 7-Day Rule Info */}
              <div className="mt-4 p-4 rounded-2xl bg-amber-50 border border-amber-200 flex items-start gap-3">
                <Info className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-amber-800 font-semibold">7-Day Rule</p>
                  <p className="text-xs text-amber-700 mt-0.5">Availability changes must be made at least 7 days before the service date. Dates within the 7-day window are locked and shown with a clock icon.</p>
                </div>
              </div>

              {/* Upcoming Assignments */}
              {assignments.length > 0 && (
                <div className="mt-4 bg-white rounded-2xl shadow-sm border border-slate-100 p-4">
                  <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-blue-500" /> Your Upcoming Assignments
                  </h3>
                  <div className="space-y-2">
                    {assignments.filter(a => !isPast(a.date)).slice(0, 10).map((a, i) => {
                      const isUnavail = unavailableDates.includes(a.date);
                      return (
                        <div key={i} className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm ${
                          isUnavail ? 'bg-red-50 border border-red-200' : 'bg-blue-50 border border-blue-100'
                        }`}>
                          <div>
                            <span className="font-medium text-slate-800">{new Date(a.date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                            <span className="text-slate-500 mx-1.5">—</span>
                            <span className="text-slate-700">{a.role_display || a.role}</span>
                          </div>
                          {isUnavail && <span className="text-xs font-bold text-red-600 px-2 py-0.5 bg-red-100 rounded-full">UNAVAILABLE</span>}
                          {a.bible_passage && <span className="text-xs text-blue-600 hidden sm:inline">{a.bible_passage}</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default MinisterAvailability;
